import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Modal, Input, message } from "antd";
import { Image } from "react-bootstrap";
import whiteLogo from "../../Assets/logo.webp"; 
import InquirySelect from "../Contact/InquirySelect";
import ModalScrollArea from "./ModalScrollArea";
import useModalViewport from "./useModalViewport";
import {
  CONTACT_FORM_EMAIL,
  CONTACT_FORM_ERROR_MESSAGE,
  CONTACT_FORM_SUCCESS_MESSAGE,
  submitContactForm,
} from "../../utils/contactFormSubmission";
import { createInquirySubject } from "../../utils/inquirySubject";
import {
  PRODUCT_INQUIRY_FIELD_NAMES,
  normalizeProductInquiryFields,
  estimatedOrderQuantityOptions,
  needOptions,
  packagingTypeOptions,
} from "../../utils/laundrySheetInquiryFields";

import './ContactModal.scss';

const initialFormData = {
  name: "",
  email: "",
  company: "",
  productName: "",
  estimatedOrderQuantity: "Not sure yet",
  packagingType: "Need Recommendation",
  expectedLaunchDate: "",
  needs: [],
  message: "",
};

function ContactModal({ isModalOpen, handleCancel, idPrefix = "contact-modal", productName = "", productSku = "", title = "Request a Quote", mobileTypography = false }) {
  const viewportStyle = useModalViewport(isModalOpen);
  const [formData, setFormData] = useState({ ...initialFormData, productName });
  const [emailError, setEmailError] = useState("");
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const closeTimer = useRef(null);
  const inquirySession = useRef(0);
  const selectedProductSku = productName.trim() && formData.productName.trim() === productName.trim()
    ? productSku
    : "";
  const fieldIds = {
    name: `${idPrefix}-name`,
    email: `${idPrefix}-email`,
    emailError: `${idPrefix}-email-error`,
    company: `${idPrefix}-company`,
    productName: `${idPrefix}-product-name`,
    estimatedOrderQuantity: `${idPrefix}-order-quantity`,
    packagingType: `${idPrefix}-packaging-type`,
    expectedLaunchDate: `${idPrefix}-expected-launch-date`,
    needs: `${idPrefix}-needs`,
    message: `${idPrefix}-message`,
  };

  useEffect(() => {
    // A response belongs to the opening/product that sent it. Closing, reopening,
    // changing products, or unmounting must not update a newer inquiry.
    inquirySession.current += 1;
    setIsSubmitting(false);
    if (isModalOpen) {
      setStatus(null);
      setEmailError("");
      setFormData((currentData) => ({ ...currentData, productName }));
    }
    return () => {
      inquirySession.current += 1;
      window.clearTimeout(closeTimer.current);
    };
  }, [isModalOpen, productName, productSku]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox" && name === "needs") {
      setFormData((currentData) => ({
        ...currentData,
        needs: checked
          ? [...currentData.needs, value]
          : currentData.needs.filter((need) => need !== value),
      }));
      return;
    }

    setFormData((currentData) => ({ ...currentData, [name]: value }));

    if (name === "email") {
      setEmailError(validateEmail(value) ? "" : "Please enter a valid email address.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    setStatus(null);
    setEmailError("");

    if (!validateEmail(formData.email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    const formSubmission = new FormData();
    formSubmission.append("name", formData.name);
    formSubmission.append("email", formData.email);
    formSubmission.append("company", formData.company);
    formSubmission.append(PRODUCT_INQUIRY_FIELD_NAMES.productName, formData.productName);
    if (selectedProductSku) formSubmission.append(PRODUCT_INQUIRY_FIELD_NAMES.sourceProductSku, selectedProductSku);
    formSubmission.append(PRODUCT_INQUIRY_FIELD_NAMES.estimatedOrderQuantity, formData.estimatedOrderQuantity);
    formSubmission.append(PRODUCT_INQUIRY_FIELD_NAMES.packagingType, formData.packagingType);
    formSubmission.append(PRODUCT_INQUIRY_FIELD_NAMES.expectedLaunchDate, formData.expectedLaunchDate);
    formData.needs.forEach((need) => {
      formSubmission.append(PRODUCT_INQUIRY_FIELD_NAMES.needs, need);
    });
    formSubmission.append("message", formData.message);

    normalizeProductInquiryFields(formSubmission);
    formSubmission.append("from_name", "Klean Leaf Website");
    formSubmission.set("subject", createInquirySubject(formSubmission));

    setIsSubmitting(true);
    const submittedSession = inquirySession.current;
    try {
      await submitContactForm(formSubmission);
      if (submittedSession !== inquirySession.current) return;
      setStatus({ type: "success" });
      message.success(CONTACT_FORM_SUCCESS_MESSAGE);
      setFormData({ ...initialFormData, productName });
      closeTimer.current = window.setTimeout(() => handleCancel(), 1500);
    } catch (error) {
      if (submittedSession !== inquirySession.current) return;
      console.error("Contact form submission failed:", error);
      setStatus({ type: "error" });
      message.error(CONTACT_FORM_ERROR_MESSAGE);
    } finally {
      if (submittedSession === inquirySession.current) setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={title}
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      width={720}
      centered
      className={`contact-modal-wrapper${mobileTypography ? " mobile-type mobile-type-modal" : ""}`}
      wrapClassName="contact-modal-overlay"
      styles={{ wrapper: viewportStyle }}
      maskClosable
    >
      <ModalScrollArea id={`${idPrefix}-scroll-area`}>
      <div className="modal-contact">
        <div className="modal-intro">
          <Image src={whiteLogo} alt="Klean Leaf logo" className="modal-logo" />
          <p className="modal-subtitle">Tell us about your product interests and project. Our team will follow up about specifications, samples, packaging, and supply options.</p>
        </div>

        <form onSubmit={handleSubmit} className="home-contact-form">
          <div className="modal-contact-fields">
            <div className="modal-form-field">
              <label htmlFor={fieldIds.name}>Name *</label>
              <Input id={fieldIds.name} type="text" name="name" value={formData.name} onChange={handleChange} autoComplete="name" required />
            </div>

            <div className="modal-form-field">
              <label htmlFor={fieldIds.email}>Email *</label>
              <Input
                id={fieldIds.email}
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
                aria-describedby={emailError ? fieldIds.emailError : undefined}
                aria-invalid={emailError ? "true" : "false"}
                required
              />
              {emailError && <span id={fieldIds.emailError} className="error-message">{emailError}</span>}
            </div>

            <div className="modal-form-field">
              <label htmlFor={fieldIds.company}>Company</label>
              <Input id={fieldIds.company} type="text" name="company" value={formData.company} onChange={handleChange} autoComplete="organization" />
            </div>
          </div>

          <fieldset className="modal-form-fieldset">
            <legend>Product / Project Details</legend>

            <div className="modal-form-field">
              <label htmlFor={fieldIds.productName}>Product of Interest</label>
              <Input
                id={fieldIds.productName}
                type="text"
                name="productName"
                value={formData.productName}
                onChange={handleChange}
                placeholder="Product name or category"
                aria-describedby={selectedProductSku ? `${fieldIds.productName}-sku` : undefined}
              />
              {selectedProductSku && <span id={`${fieldIds.productName}-sku`} className="modal-source-product">Selected product SKU: {selectedProductSku}</span>}
            </div>

            <div className="modal-form-field">
              <label htmlFor={fieldIds.estimatedOrderQuantity}>Estimated Order Quantity</label>
              <InquirySelect
                id={fieldIds.estimatedOrderQuantity}
                name="estimatedOrderQuantity"
                value={formData.estimatedOrderQuantity}
                onChange={(value) => setFormData((current) => ({ ...current, estimatedOrderQuantity: value }))}
                options={estimatedOrderQuantityOptions}
              />
            </div>

            <div className="modal-form-field">
              <label htmlFor={fieldIds.packagingType}>Packaging Type</label>
              <InquirySelect
                id={fieldIds.packagingType}
                name="packagingType"
                value={formData.packagingType}
                onChange={(value) => setFormData((current) => ({ ...current, packagingType: value }))}
                options={packagingTypeOptions}
              />
            </div>

            <div className="modal-form-field">
              <label htmlFor={fieldIds.expectedLaunchDate}>Expected Launch Date</label>
              <Input
                id={fieldIds.expectedLaunchDate}
                type="date"
                name="expectedLaunchDate"
                value={formData.expectedLaunchDate}
                onChange={handleChange}
              />
            </div>

            <div className="modal-checkbox-field">
              <span className="modal-checkbox-label" id={`${fieldIds.needs}-label`}>What Do You Need?</span>
              <div
                className="modal-checkbox-options"
                id={fieldIds.needs}
                role="group"
                aria-labelledby={`${fieldIds.needs}-label`}
              >
                {needOptions.map((option) => (
                  <label className="modal-checkbox-option" key={option}>
                    <input
                      type="checkbox"
                      name="needs"
                      value={option}
                      checked={formData.needs.includes(option)}
                      onChange={handleChange}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>
          </fieldset>

          <div className="modal-form-field">
            <label htmlFor={fieldIds.message}>Message *</label>
            <Input.TextArea
              id={fieldIds.message}
              name="message"
              value={formData.message}
              onChange={handleChange}
              className="message-box"
              autoComplete="off"
              maxLength={2000}
              rows={2}
              autoSize
              required
            />
          </div>

          <button type="submit" className="submit-button primary-btn" disabled={isSubmitting}>
            {isSubmitting ? "Sending..." : "Send Inquiry"}
          </button>
          <p className="modal-privacy-notice">
            By submitting this form, you acknowledge our <Link to="/privacy-policy" onClick={handleCancel}>Privacy Policy</Link>. We use your details to respond to your inquiry and related business requests.
          </p>
          {status && (
            <span className={`form-result ${status.type}`} role={status.type === "error" ? "alert" : "status"}>
              {status.type === "success" ? (
                CONTACT_FORM_SUCCESS_MESSAGE
              ) : (
                <>
                  We couldn’t send your message right now. Please try again, or email us directly at{" "}
                  <a href={`mailto:${CONTACT_FORM_EMAIL}`}>{CONTACT_FORM_EMAIL}</a>.
                </>
              )}
            </span>
          )}
        </form>
      </div>
      </ModalScrollArea>
    </Modal>
  );
}

export default ContactModal;
